import React, { useState } from 'react';
import { Download, Calendar, FileText, Filter, User, BarChart } from 'lucide-react';
import { exportToCSV } from '../utils/storage';

const Reports = ({ employees, attendance, tasks }) => {
  const [reportType, setReportType] = useState('attendance');
  // Set default date range to show ALL data from 2020 onwards
  const [dateRange, setDateRange] = useState({
    start: '2020-01-01', // Start from 2020 to include all historical data
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedEmployee, setSelectedEmployee] = useState('all');

  const generateAttendanceReport = () => {
    let filteredAttendance = attendance.filter(record => {
      const recordDate = new Date(record.date);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      const dateInRange = recordDate >= startDate && recordDate <= endDate;
      const employeeMatch = selectedEmployee === 'all' || record.employeeId === selectedEmployee;
      
      return dateInRange && employeeMatch;
    });

    return filteredAttendance.map(record => {
      const employee = employees.find(emp => emp.id === record.employeeId);
      return {
        employeeName: employee ? employee.name : 'Unknown',
        employeeRole: employee ? employee.role : 'Unknown',
        department: employee ? employee.department : 'Unknown',
        date: record.date,
        status: record.status,
        dayOfWeek: new Date(record.date).toLocaleDateString('en-US', { weekday: 'long' })
      };
    });
  };

  const generateTaskReport = () => {
    let filteredTasks = tasks.filter(task => {
      // Filter by due date instead of created date for better relevance
      const taskDueDate = new Date(task.dueDate);
      const taskCreatedDate = new Date(task.createdAt || task.dueDate);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      // Include task if either due date or created date is in range
      const dateInRange = (taskDueDate >= startDate && taskDueDate <= endDate) || 
                         (taskCreatedDate >= startDate && taskCreatedDate <= endDate);
      const employeeMatch = selectedEmployee === 'all' || task.employeeId === selectedEmployee;
      
      return dateInRange && employeeMatch;
    });

    return filteredTasks.map(task => {
      const employee = employees.find(emp => emp.id === task.employeeId);
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dueDate.setHours(0, 0, 0, 0);
      
      return {
        taskTitle: task.title,
        taskDescription: task.description,
        assignedTo: employee ? employee.name : 'Unknown',
        employeeRole: employee ? employee.role : 'Unknown',
        department: employee ? employee.department : 'Unknown',
        status: task.status,
        priority: task.priority,
        createdDate: task.createdAt || 'N/A',
        dueDate: task.dueDate,
        isOverdue: dueDate < today && task.status !== 'done' ? 'Yes' : 'No'
      };
    });
  };

  const generateSummaryReport = () => {
    // For summary, calculate based on ALL data, not just filtered by date range
    // This gives a complete picture of each employee
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    
    return employees
      .filter(employee => selectedEmployee === 'all' || employee.id === selectedEmployee)
      .map(employee => {
        // Get all attendance for this employee within date range
        const employeeAttendance = attendance.filter(a => {
          const recordDate = new Date(a.date);
          return a.employeeId === employee.id && 
                 recordDate >= startDate && 
                 recordDate <= endDate;
        });
        
        // Get all tasks for this employee (filter by due date or created date)
        const employeeTasks = tasks.filter(task => {
          if (task.employeeId !== employee.id) return false;
          
          const taskDueDate = new Date(task.dueDate);
          const taskCreatedDate = new Date(task.createdAt || task.dueDate);
          
          return (taskDueDate >= startDate && taskDueDate <= endDate) || 
                 (taskCreatedDate >= startDate && taskCreatedDate <= endDate);
        });
        
        const presentDays = employeeAttendance.filter(a => a.status === 'present').length;
        const absentDays = employeeAttendance.filter(a => a.status === 'absent').length;
        const halfDays = employeeAttendance.filter(a => a.status === 'half-day').length;
        const totalMarkedDays = employeeAttendance.length;
        
        const completedTasks = employeeTasks.filter(t => t.status === 'done').length;
        const pendingTasks = employeeTasks.filter(t => t.status === 'pending').length;
        const inProgressTasks = employeeTasks.filter(t => t.status === 'in-progress').length;
        
        // Calculate overdue tasks
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const overdueTasks = employeeTasks.filter(t => {
          const dueDate = new Date(t.dueDate);
          dueDate.setHours(0, 0, 0, 0);
          return dueDate < today && t.status !== 'done';
        }).length;
        
        const attendanceRate = totalMarkedDays > 0 ? ((presentDays + halfDays * 0.5) / totalMarkedDays * 100).toFixed(1) : '0.0';
        const taskCompletionRate = employeeTasks.length > 0 ? (completedTasks / employeeTasks.length * 100).toFixed(1) : '0.0';
        
        return {
          employeeName: employee.name,
          role: employee.role,
          department: employee.department,
          email: employee.email,
          phone: employee.phone,
          joinDate: employee.joinDate,
          totalMarkedDays,
          presentDays,
          absentDays,
          halfDays,
          attendanceRate: `${attendanceRate}%`,
          totalTasks: employeeTasks.length,
          completedTasks,
          pendingTasks,
          inProgressTasks,
          overdueTasks,
          taskCompletionRate: `${taskCompletionRate}%`
        };
      });
  };

  const handleExport = () => {
    let data = [];
    let filename = '';
    
    switch (reportType) {
      case 'attendance':
        data = generateAttendanceReport();
        filename = `attendance_report_${dateRange.start}_to_${dateRange.end}.csv`;
        break;
      case 'tasks':
        data = generateTaskReport();
        filename = `task_report_${dateRange.start}_to_${dateRange.end}.csv`;
        break;
      case 'summary':
        data = generateSummaryReport();
        filename = `employee_summary_${dateRange.start}_to_${dateRange.end}.csv`;
        break;
    }
    
    if (data.length === 0) {
      alert('No data available for the selected criteria');
      return;
    }
    
    exportToCSV(data, filename);
  };

  const getReportPreview = () => {
    switch (reportType) {
      case 'attendance':
        return generateAttendanceReport().slice(0, 10);
      case 'tasks':
        return generateTaskReport().slice(0, 10);
      case 'summary':
        return generateSummaryReport();
    }
  };

  const getAttendanceStats = () => {
    const data = generateAttendanceReport();
    return {
      total: data.length,
      present: data.filter(d => d.status === 'present').length,
      absent: data.filter(d => d.status === 'absent').length,
      halfDay: data.filter(d => d.status === 'half-day').length
    };
  };

  const getTaskStats = () => {
    const data = generateTaskReport();
    return {
      total: data.length,
      completed: data.filter(d => d.status === 'done').length,
      pending: data.filter(d => d.status === 'pending').length,
      inProgress: data.filter(d => d.status === 'in-progress').length,
      overdue: data.filter(d => d.isOverdue === 'Yes').length
    };
  };

  const StatCard = ({ title, value, color, icon: Icon }) => (
    <div className={`${color} rounded-lg p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm opacity-90">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-6 h-6 opacity-80" />
      </div>
    </div>
  );

  const PreviewTable = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium text-lg mb-2">No data available for the selected criteria</p>
          <p className="text-gray-500 text-sm mb-4">
            {reportType === 'tasks' && 'Try clicking "Show All Data" to see all tasks regardless of date range'}
            {reportType === 'attendance' && 'Mark attendance for employees to see data here'}
            {reportType === 'summary' && 'Add tasks and mark attendance to see employee summaries'}
          </p>
          <button
            onClick={() => {
              setDateRange({
                start: '2020-01-01',
                end: new Date().toISOString().split('T')[0]
              });
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center space-x-2"
          >
            <Calendar className="w-4 h-4" />
            <span>Show All Data</span>
          </button>
        </div>
      );
    }

    const headers = Object.keys(data[0]);
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              {headers.map((header, index) => (
                <th key={index} className="text-left p-3 font-medium text-gray-600 capitalize">
                  {header.replace(/([A-Z])/g, ' $1').trim()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="border-b hover:bg-gray-50">
                {headers.map((header, cellIndex) => (
                  <td key={cellIndex} className="p-3 text-gray-800">
                    {row[header]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const previewData = getReportPreview();
  const attendanceStats = getAttendanceStats();
  const taskStats = getTaskStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Reports & Analytics</h2>
          <p className="text-gray-600">Export attendance and task data</p>
        </div>
        
        <button
          onClick={handleExport}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Download className="w-4 h-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Report Configuration</h3>
          <button
            onClick={() => {
              setDateRange({
                start: '2020-01-01',
                end: new Date().toISOString().split('T')[0]
              });
            }}
            className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg transition-colors flex items-center space-x-1"
          >
            <Calendar className="w-4 h-4" />
            <span>Show All Data</span>
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="attendance">Attendance Report</option>
              <option value="tasks">Task Report</option>
              <option value="summary">Employee Summary</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Employees</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
          <div>
            <span className="font-medium">Date Range:</span> {new Date(dateRange.start).toLocaleDateString()} - {new Date(dateRange.end).toLocaleDateString()}
          </div>
          <div className="flex items-center space-x-4 text-xs">
            <span className="bg-blue-50 px-2 py-1 rounded">
              <span className="font-medium">Total Employees:</span> {employees.length}
            </span>
            <span className="bg-green-50 px-2 py-1 rounded">
              <span className="font-medium">Total Attendance:</span> {attendance.length}
            </span>
            <span className="bg-purple-50 px-2 py-1 rounded">
              <span className="font-medium">Total Tasks:</span> {tasks.length}
            </span>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {reportType === 'attendance' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Records"
            value={attendanceStats.total}
            color="bg-blue-100 text-blue-800"
            icon={Calendar}
          />
          <StatCard
            title="Present"
            value={attendanceStats.present}
            color="bg-green-100 text-green-800"
            icon={User}
          />
          <StatCard
            title="Half Day"
            value={attendanceStats.halfDay}
            color="bg-yellow-100 text-yellow-800"
            icon={User}
          />
          <StatCard
            title="Absent"
            value={attendanceStats.absent}
            color="bg-red-100 text-red-800"
            icon={User}
          />
        </div>
      )}

      {reportType === 'tasks' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Tasks"
            value={taskStats.total}
            color="bg-blue-100 text-blue-800"
            icon={BarChart}
          />
          <StatCard
            title="Completed"
            value={taskStats.completed}
            color="bg-green-100 text-green-800"
            icon={BarChart}
          />
          <StatCard
            title="In Progress"
            value={taskStats.inProgress}
            color="bg-blue-100 text-blue-800"
            icon={BarChart}
          />
          <StatCard
            title="Pending"
            value={taskStats.pending}
            color="bg-gray-100 text-gray-800"
            icon={BarChart}
          />
          <StatCard
            title="Overdue"
            value={taskStats.overdue}
            color="bg-red-100 text-red-800"
            icon={BarChart}
          />
        </div>
      )}

      {/* Data Preview */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              {reportType === 'attendance' && 'Attendance Data Preview'}
              {reportType === 'tasks' && 'Task Data Preview'}
              {reportType === 'summary' && 'Employee Summary Preview'}
            </h3>
            <span className="text-sm text-gray-500">
              {previewData.length} records
              {reportType !== 'summary' && previewData.length === 10 && ' (showing first 10)'}
            </span>
          </div>
        </div>
        
        <div className="p-6">
          <PreviewTable data={previewData} />
        </div>
      </div>

      {/* Report Descriptions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Report Information</h3>
        
        <div className="space-y-4">
          <div className={`p-4 rounded-lg border-l-4 ${
            reportType === 'attendance' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
          }`}>
            <h4 className="font-medium text-gray-800">Attendance Report</h4>
            <p className="text-sm text-gray-600 mt-1">
              Detailed daily attendance records including employee information, dates, attendance status, and day of the week. Perfect for payroll and HR analysis.
            </p>
          </div>
          
          <div className={`p-4 rounded-lg border-l-4 ${
            reportType === 'tasks' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
          }`}>
            <h4 className="font-medium text-gray-800">Task Report</h4>
            <p className="text-sm text-gray-600 mt-1">
              Comprehensive task tracking data including task details, assigned employees, status, priority levels, and deadlines. Useful for project management and productivity analysis.
            </p>
          </div>
          
          <div className={`p-4 rounded-lg border-l-4 ${
            reportType === 'summary' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
          }`}>
            <h4 className="font-medium text-gray-800">Employee Summary</h4>
            <p className="text-sm text-gray-600 mt-1">
              Consolidated employee performance overview combining attendance rates, task completion rates, and key metrics. Ideal for performance reviews and team analysis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;